import ConditionalLinkWrapper from '@atoms/ConditionalLinkWrapper/ConditionalLinkWrapper';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { IImageLightBox } from '@models/IImageLightBox';
import ImageLightBoxTypeEnum from '@models/enums/ImageLightBoxTypeEnum';
import LightBoxOuter from '@molecules/LightBoxOuter/LightBoxOuter';
import MultipleAuthors from '@molecules/MultipleAuthors/MultipleAuthors';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface Props extends IImageLightBox {
  readonly contentIds: string[];
  readonly dataTestId?: string;
  readonly imageClasses?: string;
  readonly imageType?: ImageLightBoxTypeEnum;
  readonly lightboxIconBottom?: boolean;
  readonly onLightBoxOpen?: (value: boolean) => void;
  readonly showAuthorOnHover?: boolean;
  readonly showImageDataByDefault?: boolean;
}

const ImageLightBox = ({
  author,
  cameraModel,
  contentIds,
  dataEpiPropertyName,
  dataTestId = 'CT-09-Image-LightBox',
  fNumber,
  image,
  imageClasses,
  imageType = ImageLightBoxTypeEnum.metadata,
  isoSensitivity,
  language,
  lensModel,
  lightboxIconBottom = false,
  meta,
  onLightBoxOpen,
  s35mmEquivalent,
  showAuthorOnHover = false,
  showImageDataByDefault = false,
  showImageLightbox,
  shutterSpeed,
}: Props) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isLightBoxOpen, setIsLightBoxOpen] = useState(false);
  const { openImageInFullScreenAriaLabel } = useAriaLabels();

  const imageData = [
    cameraModel,
    lensModel,
    shutterSpeed,
    fNumber,
    s35mmEquivalent,
    isoSensitivity,
  ];

  return (
    <>
      {image?.desktop && (
        <div
          data-epi-property-name={dataEpiPropertyName}
          className="relative w-full overflow-hidden"
          {...(!isLightBoxOpen
            ? {
                onBlur: () => setIsHovering(false),
                onFocus: () => setIsHovering(true),
                onMouseEnter: () => setIsHovering(true),
                onMouseLeave: () => setIsHovering(false),
              }
            : {})}
          data-testid={dataTestId}
        >
          <Image
            classes="relative"
            imageClasses={imageClasses}
            image={image}
            dataTestId={`${dataTestId}-Image`}
          />

          <div className="absolute top-0 left-0 z-20 flex h-full w-full content-start items-end justify-end p-5">
            {showImageLightbox && (
              <button
                type="button"
                onClick={() => {
                  setIsLightBoxOpen(true);
                  onLightBoxOpen?.(true);
                }}
                className={classNames(
                  lightboxIconBottom ? 'bottom-0' : 'top-0',
                  'absolute right-0 m-5 ml-auto flex h-7.5 min-h-[30px] w-7.5 min-w-[30px] cursor-pointer items-center justify-center rounded-full bg-black-100 bg-opacity-50 text-lg text-white hover:bg-black-100'
                )}
                aria-label={openImageInFullScreenAriaLabel}
                data-testid={`${dataTestId}-Open-LightBox-Button`}
              >
                <Icon name="full_screen" colorClassName="text-white" />
              </button>
            )}

            {imageType === ImageLightBoxTypeEnum.metadata &&
              !showAuthorOnHover && (
                <AnimatePresence>
                  {(showImageDataByDefault || isHovering) && (
                    <motion.ul
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 30 }}
                      className="pointer-events-none hidden w-full flex-wrap gap-3 lg:flex"
                    >
                      {imageData
                        .filter((item) => item)
                        .map((item) => (
                          <li
                            key={`tag-${item}`}
                            className="body-one rounded-full bg-black-100 bg-opacity-50 px-4 py-2 text-white"
                            data-testid={`${dataTestId}-InfoPill`}
                          >
                            {item}
                          </li>
                        ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              )}

            <AnimatePresence>
              {author && showAuthorOnHover && isHovering && (
                <motion.div
                  className="absolute bottom-2.5 left-2.5 hidden items-center rounded-full bg-black-100 bg-opacity-50 p-1 lg:flex xl:bottom-5 xl:left-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  data-testid={`${dataTestId}-Author`}
                >
                  <ConditionalLinkWrapper link={author.link}>
                    {author?.thumbnail ? (
                      <Image
                        image={author.thumbnail}
                        imageClasses="h-7.5 w-7.5 rounded-[10px]"
                      />
                    ) : (
                      author?.initials && (
                        <span className="flex h-7.5 w-7.5 items-center justify-center rounded-[10px] bg-grey-100 text-grey-450 uppercase">
                          {author.initials}
                        </span>
                      )
                    )}
                  </ConditionalLinkWrapper>

                  {author?.authorName && (
                    <ConditionalLinkWrapper
                      link={author.link}
                      hasHoverUnderline
                    >
                      <p className="body-three mx-2">{author.authorName}</p>
                    </ConditionalLinkWrapper>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {imageType === ImageLightBoxTypeEnum.author && author && (
              <a
                className="absolute bottom-2.5 left-2.5 rounded-full bg-black-100 bg-opacity-50 py-1 pr-4 pl-1 xl:bottom-5 xl:left-5"
                href={author.link?.url}
                target={author.link?.target}
                onClick={() => {
                  updateDataLayer({
                    event: 'selectContent',
                    content_type: author.ambassador
                      ? 'Ambassadors'
                      : 'Creators',
                    author_name: author.authorName,
                  });
                }}
              >
                <MultipleAuthors
                  authors={[
                    {
                      name: author.authorName ?? '',
                      initials: author.initials ?? '',
                      ambassador: author.ambassador,
                      image: author.thumbnail,
                    },
                  ]}
                >
                  <div
                    className="body-two ml-[15px] text-white"
                    data-testid={`${dataTestId}-Author-Name`}
                  >
                    {author.authorName}
                  </div>
                </MultipleAuthors>
              </a>
            )}
          </div>
        </div>
      )}

      {isLightBoxOpen && (
        <LightBoxOuter
          contentIds={contentIds}
          initialIndex={contentIds.indexOf(meta?.contentLink as string)}
          language={language}
          onClose={() => {
            setIsLightBoxOpen(false);
            onLightBoxOpen?.(false);
          }}
        />
      )}
    </>
  );
};

export default ImageLightBox;
